import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/ui/card';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Switch } from '@shared/ui/switch';
import { Input } from '@shared/ui/input';
import { Label } from '@shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@shared/ui/radio-group';
import { 
  BarChart2, Building2, CreditCard, Plus, CheckCircle2, 
  Wallet, X, Info 
} from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { useTranslation } from 'react-i18next';

export const Autopay = () => {
  const { t } = useTranslation();
  const [isAutoPayEnabled, setIsAutoPayEnabled] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('chase');
  const [isAddingMethod, setIsAddingMethod] = useState(false);
  const [newMethodType, setNewMethodType] = useState('ach');

  return (
    <div className="max-w-5xl space-y-6">
      {/* Top Card: Toggle AutoPay */}
      <Card className={cn("border-gray-200 shadow-sm transition-colors", isAutoPayEnabled ? "bg-green-50/50 border-green-200" : "")}>
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center space-x-4">
            <div className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
              <BarChart2 className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3 mb-1">
                <h2 className="text-lg font-bold text-gray-900">{t("AutoPay")}</h2>
                <Badge 
                  variant={isAutoPayEnabled ? "default" : "secondary"} 
                  className={cn("text-[11px] font-medium tracking-wide", 
                    isAutoPayEnabled ? "bg-green-100 text-green-700 hover:bg-green-100 border-transparent" : "text-gray-500"
                  )}
                >
                  {isAutoPayEnabled ? t("Enabled") : t("Disabled")}
                </Badge>
              </div>
              <p className="text-sm text-gray-500">
                {isAutoPayEnabled 
                  ? t("Your bill is automatically paid on the due date each month.") 
                  : t("Enable AutoPay to never miss a bill payment.")}
              </p>
            </div>
          </div>
          <Switch 
            checked={isAutoPayEnabled} 
            onCheckedChange={setIsAutoPayEnabled} 
            className="data-[state=checked]:bg-green-600"
          />
        </div>
      </Card>

      {/* Middle Card: Payment Methods */}
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
          <CardTitle className="text-lg text-gray-900">{t("Payment Method for AutoPay")}</CardTitle>
          {!isAddingMethod && (
            <Button variant="link" className="text-blue-600 p-0 h-auto font-medium" onClick={() => setIsAddingMethod(true)}>
              <Plus className="w-4 h-4 mr-1" /> {t("Add Method")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <RadioGroup value={selectedMethod} onValueChange={setSelectedMethod} className="space-y-3">
            {/* Chase Option */}
            <div 
              className={cn("flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors", 
                selectedMethod === 'chase' ? "border-blue-500 bg-blue-50/30" : "border-gray-200 hover:border-gray-300"
              )} 
              onClick={() => setSelectedMethod('chase')}
            >
              <div className="flex items-center space-x-4">
                <RadioGroupItem value="chase" id="chase" />
                <div className="flex items-center space-x-3">
                  <Building2 className={cn("w-5 h-5", selectedMethod === 'chase' ? "text-blue-500" : "text-gray-400")} />
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">Chase Checking ••••4521</div>
                    <div className="text-xs text-gray-500">JPMorgan Chase</div>
                  </div>
                </div>
              </div>
              {selectedMethod === 'chase' && <span className="text-xs font-semibold text-blue-600">{t("Default")}</span>}
            </div>

            {/* Visa Option */}
            <div 
              className={cn("flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors", 
                selectedMethod === 'visa' ? "border-blue-500 bg-blue-50/30" : "border-gray-200 hover:border-gray-300"
              )} 
              onClick={() => setSelectedMethod('visa')}
            >
              <div className="flex items-center space-x-4">
                <RadioGroupItem value="visa" id="visa" />
                <div className="flex items-center space-x-3">
                  <CreditCard className={cn("w-5 h-5", selectedMethod === 'visa' ? "text-blue-500" : "text-gray-400")} />
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">Visa ••••8842</div>
                    <div className="text-xs text-gray-500">Visa Credit Card</div>
                  </div>
                </div>
              </div>
              {selectedMethod === 'visa' && <span className="text-xs font-semibold text-blue-600">{t("Default")}</span>}
            </div>
          </RadioGroup>

          {!isAddingMethod ? (
            <Button variant="outline" className="w-full border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 h-12 mt-2" onClick={() => setIsAddingMethod(true)}>
              <Plus className="w-4 h-4 mr-2" /> {t("Add Payment Method")}
            </Button>
          ) : (
            <div className="mt-6 border border-gray-200 rounded-lg p-6 bg-gray-50/50">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-semibold text-gray-900">{t("Add New Payment Method")}</h3>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-600" onClick={() => setIsAddingMethod(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div 
                  className={cn("p-4 border rounded-lg cursor-pointer transition-colors", newMethodType === 'ach' ? "border-blue-500 bg-white shadow-sm" : "border-gray-200 bg-white hover:border-gray-300")} 
                  onClick={() => setNewMethodType('ach')}
                >
                  <div className="flex items-center space-x-3 mb-1">
                    <Building2 className={cn("w-4 h-4", newMethodType === 'ach' ? "text-blue-600" : "text-gray-400")} />
                    <div className="font-semibold text-sm text-gray-900">{t("Ach / E-Check")}</div>
                  </div>
                  <div className="text-xs text-gray-500 ml-7">{t("Bank transfer — no fee")}</div>
                </div>
                
                <div 
                  className={cn("p-4 border rounded-lg cursor-pointer transition-colors", newMethodType === 'cc' ? "border-blue-500 bg-white shadow-sm" : "border-gray-200 bg-white hover:border-gray-300")} 
                  onClick={() => setNewMethodType('cc')}
                >
                  <div className="flex items-center space-x-3 mb-1">
                    <CreditCard className={cn("w-4 h-4", newMethodType === 'cc' ? "text-blue-600" : "text-gray-400")} />
                    <div className="font-semibold text-sm text-gray-900">{t("Credit Card")}</div>
                  </div>
                  <div className="text-xs text-gray-500 ml-7">{t("Visa, MC, Discover, Amex")}</div>
                </div>
                
                <div 
                  className={cn("p-4 border rounded-lg cursor-pointer transition-colors", newMethodType === 'dc' ? "border-blue-500 bg-white shadow-sm" : "border-gray-200 bg-white hover:border-gray-300")} 
                  onClick={() => setNewMethodType('dc')}
                >
                  <div className="flex items-center space-x-3 mb-1">
                    <CreditCard className={cn("w-4 h-4", newMethodType === 'dc' ? "text-blue-600" : "text-gray-400")} />
                    <div className="font-semibold text-sm text-gray-900">{t("Debit Card")}</div>
                  </div>
                  <div className="text-xs text-gray-500 ml-7">{t("All major US banks")}</div>
                </div>
                
                <div 
                  className={cn("p-4 border rounded-lg cursor-pointer transition-colors", newMethodType === 'dw' ? "border-blue-500 bg-white shadow-sm" : "border-gray-200 bg-white hover:border-gray-300")} 
                  onClick={() => setNewMethodType('dw')}
                >
                  <div className="flex items-center space-x-3 mb-1">
                    <Wallet className={cn("w-4 h-4", newMethodType === 'dw' ? "text-blue-600" : "text-gray-400")} />
                    <div className="font-semibold text-sm text-gray-900">{t("Digital Wallet")}</div>
                  </div>
                  <div className="text-xs text-gray-500 ml-7">{t("Apple Pay / Google Pay")}</div>
                </div>
              </div>

              {newMethodType === 'ach' && (
                <div className="bg-green-50/50 border border-green-200 rounded-md p-3 flex items-center space-x-2.5 mb-6">
                  <Info className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">{t("Ach bank transfers have no convenience fee.")}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-700">{t("Routing Number")} <span className="text-red-500">*</span></Label>
                    <Input placeholder={t("9-digit ABA")} className="bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-700">{t("Account Number")} <span className="text-red-500">*</span></Label>
                    <Input placeholder={t("Account number")} className="bg-white" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">{t("Account Holder Name")} <span className="text-red-500">*</span></Label>
                  <Input placeholder={t("Full name on bank account")} className="bg-white" />
                </div>
                
                <div className="flex items-center space-x-3 pt-4">
                  <Button variant="outline" onClick={() => setIsAddingMethod(false)} className="w-24 bg-white border-gray-300">
                    {t("Cancel")}
                  </Button>
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> {t("Save Payment Method")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Card: Schedule info */}
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg text-gray-900">{t("AutoPay Schedule")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
              <div className="text-[11px] font-medium text-gray-500 mb-1.5 tracking-wider">{t("Payment Timing")}</div>
              <div className="font-semibold text-gray-900 mb-1">{t("On Due Date")}</div>
              <div className="text-xs text-gray-500">{t("Payment processed on bill due date")}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
              <div className="text-[11px] font-medium text-gray-500 mb-1.5 tracking-wider">{t("Billing Cycle")}</div>
              <div className="font-semibold text-gray-900 mb-1">{t("Monthly")}</div>
              <div className="text-xs text-gray-500">{t("Bills generated on the 1st of the month")}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
              <div className="text-[11px] font-medium text-gray-500 mb-1.5 tracking-wider">{t("Notification")}</div>
              <div className="font-semibold text-gray-900 mb-1">{t("3 days before")}</div>
              <div className="text-xs text-gray-500">{t("Email/SMS reminder before processing")}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Autopay;
